import { IncentiveName } from "models";

export const PROGRAM_TYPE = {
  INSETTING: "insetting",
  OFFSETTING: "offsetting",
};

export type ProgramType = (typeof PROGRAM_TYPE)[keyof typeof PROGRAM_TYPE];

export const CARBON_RUNNER_ERROR_TYPE = {
  FOOTPRINT_SERVICE: "Footprint Service Error",
};

export type CarbonRunnerErrorType = (typeof CARBON_RUNNER_ERROR_TYPE)[keyof typeof CARBON_RUNNER_ERROR_TYPE];

export type ProgramTermsConsent = {
  userId: string;
  contractId?: string;
  programId?: string;
  signedAt?: Date;
  declinedAt?: Date;
};

export type RangeObject = {
  min: number;
  max: number;
  avg: number;
};

export type PointsPerCategory = {
  removals: RangeObject;
  avoidances: RangeObject;
  reductions: RangeObject;
};

export type NullableRangeObject = {
  min: number | null;
  max: number | null;
  avg: number | null;
};

type SocAndEmissionObject = {
  soc: NullableRangeObject;
  emission: NullableRangeObject;
};

type AgronomicalEvaluationMetricsObject = {
  soc: NullableRangeObject;
  humus: NullableRangeObject;
  co2: NullableRangeObject;
};

export type FootprintAggregation = {
  /** The CO2e Delta for soc and emission for the whole field (not /ha)
   * Total amount is `emission + soc` for min, max and avg.
   * Total footprint is `amount - penalty` for min, max and avg. */
  amount: SocAndEmissionObject;

  /** Penalty to for soc and emission.
   * Total penalty is `emission + soc` for min, max and avg.
   * Total footprint is `amount - penalty` for min, max and avg. */
  penalty: SocAndEmissionObject;
};

export const INVALID_REASONS = {
  MISSING_DEPENDENCY: "MISSING_DEPENDENCY",
  NO_ENTRIES: "NO_ENTRIES",
  NO_MAIN_CROP: "NO_MAIN_CROP",
} as const;

export type InvalidReason = (typeof INVALID_REASONS)[keyof typeof INVALID_REASONS];

export const PRACTICE_SLUG = {
  CROPS: "crops",
  MINERAL_FERTILIZERS: "mineralFertilizers",
  ORGANIC_FERTILIZERS: "organicFertilizers",
  PLANT_PROTECTIONS: "plantProtections",
  SEEDINGS: "seedings",
  TILLAGES: "tillages",
  LAND_USE_CHANGES: "landUseChanges",
} as const;

export const DAIRY_PRACTICE_SLUG = {
  FEED_ADDITIVES: "feedAdditives",
  MANURE_STORAGE: "storageTypes",
  MANURE_ADDITIVES: "manureAdditives",
} as const;

// this is a duplicate, and is only here because ts-jest couldn't find it for some reason
enum PRACTICE_BUNDLE_SLUG {
  CROPS = "crops",
  FERTILIZER = "fertilizer",
  TILLAGE = "tillage",
}

export type PracticeSlug = (typeof PRACTICE_SLUG)[keyof typeof PRACTICE_SLUG];

export const PRACTICE_TO_BUNDLE_MAP = {
  [PRACTICE_SLUG.CROPS]: PRACTICE_BUNDLE_SLUG.CROPS,
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: PRACTICE_BUNDLE_SLUG.FERTILIZER,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: PRACTICE_BUNDLE_SLUG.FERTILIZER,
  [PRACTICE_SLUG.TILLAGES]: PRACTICE_BUNDLE_SLUG.TILLAGE,
  [PRACTICE_SLUG.SEEDINGS]: PRACTICE_BUNDLE_SLUG.TILLAGE,
} as const;

export type PracticeToBundleMap = (typeof PRACTICE_TO_BUNDLE_MAP)[keyof typeof PRACTICE_TO_BUNDLE_MAP];

export type ValidAggregation = {
  validation: {
    valid: true;
  };
  isBaselineYear: boolean;
  footprints: FootprintAggregation;
  // Points and payouts optional because if the baseline is invalid non-baseline years won't include points/payouts
  points?: PointsPerCategory;
  payouts?: RangeObject;
};

export type ValidAggregationWithPayouts = Required<ValidAggregation>;

type InvalidAggregation = {
  validation: {
    valid: false;
    reason: InvalidReason;
    missingDependencies: PracticeSlug[];
  };
  isBaselineYear: boolean;
};

export type ValidOrInvalidAggregation = ValidAggregation | InvalidAggregation;

export type AggregationsByYear = {
  [year: string]: ValidAggregation | InvalidAggregation;
};

export type FieldOutcomesByPractice = {
  [k in PracticeSlug]?: {
    isParticipating: boolean;
    required: {
      byProgram: boolean; // program requirement
      byDependency: boolean; // dependency requirement
      byContinuation: boolean; // practice is continued (not start year)
    };
    years: AggregationsByYear;
  };
};

export type AgronomicalEvaluationAggregationsByYear = {
  [year: string]: AgronomicalEvaluationMetricsObject;
};

export type SingleFieldOutcomesWithError = {
  fieldId: string;
  error: Error;
};

export type IncentiveData = {
  amountPerHa: number;
  paymentBasis: string;
  hectares: number;
  total: number;
  practiceSlug: PracticeSlug;
};

export type EarnedIncentivesAndTotalPayout = {
  earnedIncentives: {
    [incentiveName: string]: IncentiveData;
  };
  earnedTotalPayout: number;
};

export type SingleFieldOutcomes = {
  fieldId: string;
  fieldOutcomes: FieldOutcomesByPractice;
  agronomicalEvaluationMetrics: AgronomicalEvaluationAggregationsByYear;
  practiceIncentives: EarnedIncentivesAndTotalPayout | null;
  potentialPayout: number | null;
};

export type CarbonProgramResult = SingleFieldOutcomes | SingleFieldOutcomesWithError;

export type CarbonProgramOutcomesResponse = {
  [fieldId: string]: CarbonProgramResult;
};

export type PayoutsByField = {
  [fieldId: string]: {
    totalPayout: number;
    practicePayouts: Record<PracticeSlug, number>;
  };
};

export type IncentiveDataMap = Record<IncentiveName, IncentiveData>;

export type IncentivesPotentialPayouts = {
  practicePotentialIncentives: IncentiveDataMap;
  practicePotentialTotalPayout: number;
  projectAreaHa: number;
};

export type ProgramContract = {
  id: string;
  link: string;
  programId: string;
  replacedById: string | null;
  createdAt: string;
};
